import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Heart, Share2, Twitter, Facebook, Linkedin, Link2, Check, BookOpen, Clock, PenTool, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { getMarketingSiteApiUrl } from '../utils/api';
import DOMPurify from 'dompurify';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // Resolve image URLs - convert production domain to relative path on non-production environments
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (typeof window !== 'undefined' && !window.location.hostname.includes('popcrownofficial.com')) {
      return url.replace('https://popcrownofficial.com', '');
    }
    return url;
  };
  const [error, setError] = useState(null);

  // Fetch post from API
  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const baseUrl = getMarketingSiteApiUrl();
        const response = await fetch(`${baseUrl}/api/blog/posts/${slug}?format=html`);
        if (!response.ok) throw new Error('Post not found');
        const data = await response.json();
        setPost(data);
        setError(null);
      } catch (err) {
        setError('Post not found');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // State for likes and share menu
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate or retrieve visitor ID for tracking likes
  const getVisitorId = () => {
    let visitorId = localStorage.getItem('popcrown_visitor_id');
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('popcrown_visitor_id', visitorId);
    }
    return visitorId;
  };

  // Get API base URL
  const getApiUrl = useCallback(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname.includes('preview.emergentagent.com')) {
        return process.env.REACT_APP_BACKEND_URL || '';
      }
    }
    return 'https://popcrownofficial.com';
  }, []);

  // Load likes from API
  useEffect(() => {
    const fetchLikes = async () => {
      if (!slug) return;
      try {
        const visitorId = getVisitorId();
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/api/blog/likes/${slug}?visitor_id=${visitorId}`);
        if (response.ok) {
          const data = await response.json();
          setLikeCount(data.like_count);
          setLiked(data.user_liked);
        }
      } catch (_) {
        // Silently fail - likes are non-critical
      }
    };
    fetchLikes();
  }, [slug, getApiUrl]);

  // Calculate reading time
  const getReadingTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  };

  // Handle like - toggle via API
  const handleLike = async () => {
    if (likeLoading) return;
    setLikeLoading(true);
    
    try {
      const visitorId = getVisitorId();
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/blog/likes/${slug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ visitor_id: visitorId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setLikeCount(data.like_count);
        setLiked(data.user_liked);
      }
    } catch (_) {
      // Silently fail - likes are non-critical
    } finally {
      setLikeLoading(false);
    }
  };

  // Share URLs - Use the API share endpoint for social media sharing
  const productionUrl = `https://popcrownofficial.com/api/blog/share/${slug}`;
  const shareUrl = productionUrl;
  const displayUrl = `https://popcrownofficial.com/blog/${slug}`;
  const shareTitle = post?.title || 'PopCrown Blog';
  const shareText = post?.metaDescription || post?.content?.substring(0, 150) || '';

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  // Copy link to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Clipboard API not available
    }
  };

  // Generate meta description
  const getMetaDescription = () => {
    if (post?.metaDescription) return post.metaDescription;
    if (post?.content) {
      const firstParagraph = post.content.split('\n\n')[0];
      return firstParagraph.substring(0, 160) + '...';
    }
    return 'Read the latest articles from PopCrown - your safe space for pop culture fans worldwide.';
  };

  // Full URL for sharing and SEO - always use production domain for canonical
  const currentUrl = `https://popcrownofficial.com/blog/${slug}`;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#DC143C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!post || error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Helmet>
          <title>Post Not Found | PopCrown Blog</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="btn-gold">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    if (!content) return null;
    const blocks = content.split('\n\n');
    const result = [];
    let i = 0;
    
    while (i < blocks.length) {
      const paragraph = blocks[i];
      
      // Handle H2 headings (## Header or <h2>Header</h2>)
      if (paragraph.startsWith('## ')) {
        result.push(
          <h2 key={i} className="text-2xl md:text-3xl font-bold text-black mt-8 mb-4">
            {paragraph.replace('## ', '')}
          </h2>
        );
        i++;
        continue;
      }
      if (paragraph.startsWith('<h2>')) {
        result.push(
          <h2 key={i} className="text-2xl md:text-3xl font-bold text-black mt-8 mb-4">
            {paragraph.replace(/<\/?h2>/g, '')}
          </h2>
        );
        i++;
        continue;
      }
      
      // Handle H3 headings (### Header or <h3>Header</h3>)
      if (paragraph.startsWith('### ')) {
        result.push(
          <h3 key={i} className="text-xl font-bold text-black mt-6 mb-3">
            {paragraph.replace('### ', '')}
          </h3>
        );
        i++;
        continue;
      }
      if (paragraph.startsWith('<h3>')) {
        result.push(
          <h3 key={i} className="text-xl font-bold text-black mt-6 mb-3">
            {paragraph.replace(/<\/?h3>/g, '')}
          </h3>
        );
        i++;
        continue;
      }
      
      // Handle H3 headings (bold only lines - legacy support)
      if (paragraph.startsWith('**') && paragraph.endsWith('**') && !paragraph.includes('\n')) {
        result.push(
          <h3 key={i} className="text-xl font-bold text-black mt-6 mb-3">
            {paragraph.replace(/\*\*/g, '')}
          </h3>
        );
        i++;
        continue;
      }
      
      // Handle numbered lists
      if (/^\d+\.\s/.test(paragraph) || paragraph.split('\n').some(line => /^\d+\.\s/.test(line.trim()))) {
        const lines = paragraph.split('\n');
        const listItems = lines.filter(line => /^\d+\.\s/.test(line.trim()));
        if (listItems.length > 0) {
          result.push(
            <ol key={i} className="list-decimal list-inside space-y-2 mb-6 ml-4">
              {listItems.map((item, idx) => {
                const itemContent = item.trim().replace(/^\d+\.\s/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                return (
                  <li key={idx} className="text-gray-700 text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(itemContent) }} />
                );
              })}
            </ol>
          );
          i++;
          continue;
        }
      }
      
      // Handle bullet lists
      const lines = paragraph.split('\n');
      const bulletLines = lines.filter(line => line.trim().startsWith('- '));
      if (bulletLines.length > 0) {
        const introLines = lines.filter(line => !line.trim().startsWith('- ') && line.trim() !== '');
        if (introLines.length > 0) {
          const introText = introLines.join(' ').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          result.push(
            <p key={`${i}-intro`} className="text-gray-700 text-lg leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(introText) }} />
          );
        }
        result.push(
          <ul key={i} className="list-disc list-inside space-y-2 mb-6 ml-4">
            {bulletLines.map((item, idx) => {
              const itemContent = item.replace(/^- /, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              return (
                <li key={idx} className="text-gray-700 text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(itemContent) }} />
              );
            })}
          </ul>
        );
        i++;
        continue;
      }
      
      // Handle HTML blocks (divs, iframes, links, styled elements)
      if (paragraph.startsWith('<div') || paragraph.startsWith('<iframe') || paragraph.startsWith('<a ')) {
        result.push(
          <div key={i} className="mb-6" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(paragraph, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'style', 'width', 'height'] }) }} />
        );
        i++;
        continue;
      }
      
      // Handle regular paragraphs (strip <p> tags from API HTML content)
      const cleanParagraph = paragraph.replace(/^<p>(.*)<\/p>$/s, '$1');
      const formattedParagraph = cleanParagraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      result.push(
        <p key={i} className="text-gray-700 text-lg leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formattedParagraph) }} />
      );
      i++;
    }
    
    return result;
  };

  return (
    <div className="min-h-screen pt-20" style={{
      background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 50%, #FFFFFF 100%)'
    }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | PopCrown Blog</title>
        <meta name="description" content={getMetaDescription()} />
        <meta name="author" content={post.author} />
        <meta name="keywords" content={post.keywords || `Michael Jackson, MJ, King of Pop, pop culture, fan community, ${post.category}, PopCrown`} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:image" content={`https://popcrownofficial.com/api/blog/og-image/${post.slug}.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="PopCrown" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={getMetaDescription()} />
        <meta name="twitter:image" content={`https://popcrownofficial.com/api/blog/og-image/${post.slug}.png`} />
        
        {/* Schema.org Article JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "url": currentUrl,
            "description": getMetaDescription(),
            "image": {
              "@type": "ImageObject",
              "url": post.image?.startsWith('http') ? post.image : `https://popcrownofficial.com${post.image}`,
              "width": 1200,
              "height": 630
            },
            "author": {
              "@type": "Person",
              "name": post.author,
              "url": "https://popcrownofficial.com/about"
            },
            "publisher": {
              "@type": "Organization",
              "name": "PopCrown Official",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_poplegacy/artifacts/p5j56ube_Main%20Logo%20Name%20%282%29.png"
              }
            },
            "datePublished": `${post.date}T10:00:00+00:00`,
            "dateModified": `${post.date}T10:00:00+00:00`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": currentUrl
            },
            "keywords": post.keywords || `Michael Jackson, MJ fans, ${post.category}, PopCrown`,
            "articleSection": post.category,
            "inLanguage": "en"
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-[#DC143C] hover:text-[#E10600] transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold">Back to Blog</span>
        </Link>

        {/* Article */}
        <article className="max-w-4xl mx-auto">
          {/* Header Image */}
          <div className="relative h-96 rounded-xl overflow-hidden mb-2">
            <img 
              src={resolveImageUrl(post.image)}
              alt={post.imageAlt || `${post.title} - Michael Jackson fan article`}
              className="w-full h-full object-cover"
              style={{ objectPosition: post.imagePosition || 'center' }}
            />
            <div className="absolute top-6 left-6">
              <span className="bg-[#E10600] text-black px-4 py-2 rounded-full text-sm font-bold">
                {post.category}
              </span>
            </div>
          </div>
          {/* Image Credit */}
          {post.imageCredit && (
            <p className="text-center text-gray-500 text-xs mb-8">
              Image: {post.imageCreditUrl ? (
                <a href={post.imageCreditUrl} target="_blank" rel="noopener noreferrer" className="text-[#DC143C] hover:underline">
                  {post.imageCredit}
                </a>
              ) : post.imageCredit}
            </p>
          )}

          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-black mb-6">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-gray-600 mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#E10600]" />
                <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#E10600]" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#E10600]" />
                <span>{getReadingTime(post.content)} min read</span>
              </div>
            </div>
            
            {/* Like and Share Bar */}
            <div className="flex items-center justify-between py-4 border-t border-b border-gray-200">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleLike}
                  disabled={likeLoading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    liked 
                      ? 'bg-red-100 text-[#DC143C]' 
                      : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-[#DC143C]'
                  } ${likeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={liked ? 'Unlike this post' : 'Like this post'}
                  data-testid="like-button"
                >
                  <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''} ${likeLoading ? 'animate-pulse' : ''}`} />
                  <span className="font-medium" data-testid="like-count">{likeCount > 0 ? likeCount : 'Like'}</span>
                </button>
              </div>

              {/* Share Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm hidden sm:inline">Share:</span>
                <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 hover:bg-[#1DA1F2] hover:text-white transition-all" aria-label="Share on Twitter">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 hover:bg-[#4267B2] hover:text-white transition-all" aria-label="Share on Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 hover:bg-[#0A66C2] hover:text-white transition-all" aria-label="Share on LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </a>
                <button onClick={copyToClipboard} className={`p-2 rounded-full transition-all ${copied ? 'bg-green-100 text-green-600' : 'bg-gray-100 hover:bg-[#E10600] hover:text-black'}`} aria-label="Copy link">
                  {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {renderContent(post.content)}
          </div>

          {/* Community Author Card — only on "From the Community" posts */}
          {post.category === 'From the Community' && (
            <aside
              className="mt-12 rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF7F7 100%)',
                border: '1px solid rgba(225, 6, 0, 0.15)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.06)',
              }}
              data-testid="community-author-card"
              aria-label="About the author"
            >
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4 md:gap-5">
                  {/* Initials avatar (falls back automatically if no photo asset) */}
                  <div
                    className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white font-bold text-xl md:text-2xl"
                    style={{ background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)' }}
                    aria-hidden="true"
                  >
                    {(post.author || 'PC').split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#E10600] mb-1 inline-flex items-center gap-1.5">
                      <PenTool className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
                      Written by a PopCrown Member
                    </p>
                    <h3 className="text-lg md:text-xl font-bold text-black mb-1 break-words">
                      {post.author || 'A PopCrown Member'}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      This story was written by a member of the global PopCrown community — real fans, real voices, no ghost-writers, no rewrites. If Michael Jackson's music has shaped your story too, we'd love to hear it.
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-[#E10600]/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-black mb-0.5">Want to write for PopCrown?</p>
                    <p className="text-sm text-gray-500">Send us a pitch or a finished piece — we publish member stories every week.</p>
                  </div>
                  <a
                    href="mailto:support@popcrownofficial.com?subject=Write%20for%20PopCrown%20%E2%80%94%20Member%20Story%20Pitch&body=Hi%20PopCrown%20team%2C%0A%0AI%27d%20love%20to%20write%20for%20the%20community%20blog.%20Here%27s%20a%20quick%20pitch%20or%20my%20draft%3A%0A%0A"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm bg-black text-white hover:bg-[#E10600] transition-colors whitespace-nowrap"
                    data-testid="community-write-for-us-btn"
                  >
                    <Mail className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                    Write For Us
                  </a>
                </div>
              </div>
            </aside>
          )}

          {/* Bottom Engagement Bar */}
          <div className="mt-10 p-6 bg-gray-50 rounded-xl">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-gray-600">Did you enjoy this article?</span>
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold transition-all ${
                    liked 
                      ? 'bg-[#DC143C] text-white' 
                      : 'bg-white border-2 border-[#DC143C] text-[#DC143C] hover:bg-[#DC143C] hover:text-white'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                  {liked ? 'Liked!' : 'Like this post'}
                  {likeCount > 0 && <span className="ml-1">({likeCount})</span>}
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-gray-600">Share:</span>
                <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full bg-white border border-gray-200 hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2] transition-all">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full bg-white border border-gray-200 hover:bg-[#4267B2] hover:text-white hover:border-[#4267B2] transition-all">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full bg-white border border-gray-200 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all">
                  <Linkedin className="w-5 h-5" />
                </a>
                <button onClick={copyToClipboard} className={`p-2.5 rounded-full border transition-all ${copied ? 'bg-green-500 text-white border-green-500' : 'bg-white border-gray-200 hover:bg-[#E10600] hover:border-[#E10600]'}`}>
                  {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="mt-12 p-8 rounded-xl text-center" style={{
            background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.1) 0%, rgba(220, 20, 60, 0.1) 100%)',
            border: '2px solid #E10600'
          }}>
            <h3 className="text-2xl font-bold text-black mb-4">
              Want More Fan Stories?
            </h3>
            <p className="text-gray-700 mb-6">
              Subscribe to our newsletter for weekly updates, exclusive content, and the latest from the PopCrown community.
            </p>
            <Link to="/" className="btn-gold">
              Subscribe to Newsletter
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
